import type { StakesConfig, ScoringType } from '@/lib/games/engine/types';
import {
  CATEGORY_LABELS,
  CATEGORY_ORDER,
  type CatalogEntry,
  isGameAvailable,
  pickerCatalogEntries,
  catalogLabel,
} from '@/lib/games/game-catalog';
import { listGamePlugins } from '@/lib/games/engine/registry';

interface Props {
  enabled: Record<string, boolean>;
  onToggle: (id: string) => void;
  playerCount: number;
  onRound?: Record<string, boolean>;
}

export function CategorizedGamePicker({ enabled, onToggle, playerCount, onRound }: Props) {
  const plugins = listGamePlugins();
  const entries = pickerCatalogEntries().filter((g) => !g.eventTemplate);

  const byCategory = CATEGORY_ORDER.map((cat) => ({
    cat,
    label: CATEGORY_LABELS[cat],
    items: entries.filter((e) => e.category === cat),
  })).filter((s) => s.items.length);

  const renderCard = (entry: CatalogEntry) => {
    const available = isGameAvailable(entry, playerCount);
    const on = !!enabled[entry.id];
    const onRoundGame = !!onRound?.[entry.id];
    const plugin = plugins.find((p) => p.id === entry.pluginId);
    return (
      <button
        key={entry.id}
        type="button"
        disabled={!available}
        onClick={() => available && onToggle(entry.id)}
        className={`flex flex-col gap-1 rounded-[14px] border-2 p-3.5 text-left transition-colors ${
          !available ? 'cursor-not-allowed opacity-45' : on ? 'border-navy bg-navy/5' : 'border-line bg-paper'
        }`}
      >
        <div className="flex items-center justify-between gap-2">
          <span className="font-serif text-sm font-bold text-ink">{entry.label}</span>
          <span className={`text-lg ${on ? 'text-good' : 'text-line'}`}>{on ? '☑' : '☐'}</span>
        </div>
        <p className="text-[11px] leading-snug text-ink-soft">
          {!available
            ? `Needs ${entry.minPlayers}${entry.maxPlayers < 4 ? `–${entry.maxPlayers}` : '+'} players`
            : onRoundGame && on
              ? 'On this round — tap to remove'
              : plugin?.description || entry.description}
        </p>
      </button>
    );
  };

  return (
    <div className="space-y-4">
      {byCategory.map(({ cat, label, items }) => (
        <div key={cat}>
          <div className="mb-2 text-xs font-bold uppercase tracking-wide text-navy">{label}</div>
          <div className="grid grid-cols-2 gap-2.5">
            {items.map(renderCard)}
          </div>
        </div>
      ))}
    </div>
  );
}

export function defaultEnabledGames(): Record<string, boolean> {
  return {};
}

export function catalogGameLabel(id: string): string {
  return catalogLabel(id);
}
